import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CmsApi, SiteConfigManager, useSettings } from '@atlas/core';
import { Loader } from '@atlas/ui';

const defaultState = {
  content: null,
  error: null,
  loading: true,
  resolved: false,
};

const StaticBlock = ({ className = '', fallback = null, onClick = () => {}, options = {} }) => {
  const [state, setState] = useState(defaultState);
  const [DOMPurify, installDOMPurify] = useState({});
  const { lang } = useSettings();

  const handleStaticDataLoaded = useCallback(
    res => {
      if (res) {
        setState(state => ({
          ...state,
          content: res.data,
          loading: false,
          resolved: true,
        }));
      }
    },
    [setState],
  );

  const handleStaticDataError = useCallback(
    staticError => {
      const { error } = staticError;

      setState(state => ({
        ...state,
        error,
        loading: false,
        resolved: true,
      }));
    },
    [setState],
  );

  useEffect(() => {
    import('dompurify').then(DOMPurifyModule => {
      installDOMPurify({ ...DOMPurifyModule.default });
    });
  }, []);

  useEffect(() => {
    if (SiteConfigManager.getConfig('config.config.useStaticBlocksFromConfig', false)) {
      handleStaticDataLoaded({
        data:
          SiteConfigManager.getConfig(`config.staticBlocks.${options.source}.i18n.${lang}.text`) ||
          SiteConfigManager.getConfig(`config.staticBlocks.${options.source}.i18n.ENG.text`) ||
          '',
      });

      return;
    }

    CmsApi.getStaticBlock({
      lang,
      source: options.source,
    })
      .then(handleStaticDataLoaded)
      .catch(handleStaticDataError);
  }, [handleStaticDataError, handleStaticDataLoaded, options.source, lang]);

  const renderStaticContent = useCallback(
    content => {
      if (!DOMPurify.sanitize) return null;
      const cleanHtml = { __html: DOMPurify.sanitize(content, { FORCE_BODY: true }) };
      return (
        <div
          className={cx('static', className)}
          dangerouslySetInnerHTML={cleanHtml}
          onClick={onClick}
          role="button"
        />
      );
    },
    [DOMPurify, className, onClick],
  );

  const renderView = useMemo(() => {
    const { content, error, loading, resolved } = state;

    if (loading || (resolved && !renderStaticContent)) {
      return <Loader />;
    }

    if (resolved) {
      if (error) {
        return <div>{error.message}</div>;
      }

      if (content) {
        return renderStaticContent(content);
      }
    }

    return fallback;
  }, [state, fallback, renderStaticContent]);

  return renderView;
};

StaticBlock.propTypes = {
  className: PropTypes.string,
  fallback: PropTypes.node,
  options: PropTypes.object,
};

export default memo(StaticBlock);
